import { useEffect, useMemo } from "react";
import { useLocation, useNavigationType, useParams } from "react-router";
import { useCollection, useCurrentUserID } from "@/api/hooks";
import { useGetRecordsTimeRangeQuery } from "@/api/api";
import { fitIntervalsToSeconds } from "./fitRange";
import { intervalsAreEqual } from "./intervals";
import { QuickIntervalTarget } from "./quickIntervals";
import { safeName } from "./recordTree";
import { HGInterval } from "./types";
import getGlobalStacks from "@/state/globalStacks";
import {
  getRecordsViewOptions,
  setRecordsViewOption,
} from "@/state/recordsView";

/**
 * The interval pair covering every record of one collection, or of the whole
 * space when `collectionID` is undefined. `target` is null while the range is
 * unknown — still loading, or nothing timed in scope; `hasData` tells those two
 * apart.
 *
 * The server widens the scope to the collection *and its descendants*
 * (`get_effective_collection_ids`), which is what makes this meaningful for a
 * collapsed row in the sidebar as well as for the collection page.
 */
export function useCollectionTimeRange(
  collectionID: string | undefined,
  options: { skip?: boolean } = {},
) {
  const userID = useCurrentUserID();
  // `currentData`, never `data`: through a switch from one collection to
  // another `data` still holds the collection just left, and a caller acting on
  // it fits the new page to the old page's range and then never hears the real
  // answer, having already acted.
  const { currentData: timeRange, isFetching } = useGetRecordsTimeRangeQuery(
    { userID, collectionID },
    { skip: options.skip },
  );

  const minSecond = timeRange?.min_second ?? null;
  const maxSecond = timeRange?.max_second ?? null;
  // Memoized because fitting runs the WASM engine once per level of the fit
  // chain, and because callers below want a dependency that only changes when
  // the range does.
  const target = useMemo(
    () =>
      minSecond != null && maxSecond != null
        ? fitIntervalsToSeconds(BigInt(minSecond), BigInt(maxSecond))
        : null,
    [minSecond, maxSecond],
  );

  return {
    target,
    isFetching,
    hasRange: !!minSecond,
    /** The server has answered, so a null `target` means "nothing timed". */
    hasData: !options.skip && timeRange !== undefined,
  };
}

/**
 * The interval pair covering every record of the collection currently in the
 * URL (or of the whole space when there is none).
 *
 * Keyed on the *resolved* collection id, never the URL segment: that segment may
 * be a slug or an old slug, each of which would open its own cache entry for the
 * same collection. Skipped entirely until it resolves, so the wait never shows
 * up as the whole space's range.
 */
export function useAllRecordsTarget() {
  const { collectionID: collectionRef } = useParams();
  const collectionID = useCollection()?.id;
  const resolving = !!collectionRef && !collectionID;
  const { target, isFetching, hasData } = useCollectionTimeRange(collectionID, {
    skip: resolving,
  });

  return { target, isFetching: isFetching || resolving, hasData };
}

/** Whether the range on screen is already the one covering every record. */
export function isFittedToAllRecords(
  target: QuickIntervalTarget | null,
  interval: HGInterval,
  endInterval: HGInterval | null,
): boolean {
  if (!target) return false;
  if (!intervalsAreEqual(target.interval, interval)) return false;
  return target.endInterval === null
    ? endInterval === null
    : intervalsAreEqual(target.endInterval, endInterval);
}

/**
 * Show everything the collection has: the whole span on the time axis, or the
 * notes tab when it has nothing on that axis at all. Records that are not timed
 * are invisible in every interval, so widening the range for a collection of
 * pure notes would land on an empty timeline.
 */
function applyShowAllRecords(target: QuickIntervalTarget | null) {
  if (!target) {
    setRecordsViewOption("mode", "notes");
    return;
  }
  setRecordsViewOption("mode", "time");
  getGlobalStacks().setIntervals(target.interval, target.endInterval);
}

/**
 * Pressing the collection that is already open. It widens the range to fit every
 * record; once the view is already showing all of them — the range fits, or the
 * collection has nothing timed to fit — it flips to the notes tab instead, and
 * back again from there.
 *
 * Null while there is nothing at all to do, so callers can leave the click as a
 * plain no-op.
 */
export function useShowAllRecords() {
  const { collectionID: collectionRef } = useParams();
  const { target, hasData } = useAllRecordsTarget();
  // Notes live in a collection, so outside one there is no tab to flip to and
  // the press is only ever a fit — the same derivation Records makes.
  const hasNotesTab = !!collectionRef;

  return useMemo(() => {
    if (!target && !hasNotesTab) return null;

    return () => {
      const stacks = getGlobalStacks();
      // Read at click time rather than through the subscribing hooks: the
      // sidebar this hangs off must not re-render on every time navigation.
      const inNotes = hasNotesTab && getRecordsViewOptions().mode === "notes";
      if (inNotes) {
        // Back to the timeline, fitted in the same press. Nothing timed to fit
        // still leaves notes, so the flip is never a dead press.
        setRecordsViewOption("mode", "time");
        if (target) stacks.setIntervals(target.interval, target.endInterval);
        return;
      }

      const fitted = isFittedToAllRecords(
        target,
        stacks.getInterval(),
        stacks.getEndInterval(),
      );
      // A range still in flight is not the same as no range: flipping to notes
      // on it would make a press during the first moments of a page do the
      // opposite of what the same press does a moment later.
      if (!target && !hasData) return;
      if (!target || fitted) {
        if (hasNotesTab) setRecordsViewOption("mode", "notes");
        return;
      }
      stacks.setIntervals(target.interval, target.endInterval);
    };
  }, [target, hasData, hasNotesTab]);
}

/** `/{user}/{collection}` — the one shape of URL this applies to. */
function isCollectionPath(pathname: string) {
  const segments = pathname.split("/").filter(Boolean);
  // "-" is the reserved record segment, "record" the legacy permalink.
  return (
    segments.length === 2 && segments[0] !== "record" && segments[1] !== "-"
  );
}

/**
 * Armed once per page load, and only when the app booted straight onto a
 * collection page. Module-level rather than a ref because it is a property of
 * the page load, not of any component: the Records instance that consumes it
 * may not be the one mounted at boot.
 */
let pendingBootFit = isCollectionPath(window.location.pathname);

/**
 * A link opened cold lands on a collection showing all of its records: the range
 * fits its whole span, or the notes tab opens when it has nothing on the time
 * axis. Without this the page opens on wherever the time cursor happened to be —
 * "today" in a fresh tab — and a collection whose records sit in 1789 reads as
 * empty.
 *
 * Only the page the app *booted* on. Picking another collection inside a running
 * app deliberately keeps the interval, which is what makes browsing several
 * collections over one span possible at all; pressing the collection that is
 * already open is how you ask for its own span there (`useShowAllRecords`).
 *
 * A reload is left alone too: it arrives with the stacks in its history entry
 * and in sessionStorage, and re-fitting would throw away the place the user was
 * reloading.
 */
export function useShowAllRecordsOnBoot() {
  const collectionID = useCollection()?.id;
  const { target, hasData } = useAllRecordsTarget();
  const navigationType = useNavigationType();
  const restored = !!useLocation().state?.stacks;

  useEffect(() => {
    if (!pendingBootFit) return;
    if (navigationType === "PUSH") {
      // Navigated on before the range came back; the boot page is over.
      pendingBootFit = false;
      return;
    }
    if (!collectionID) return;
    // Waiting for the answer, not for a range: a collection with nothing timed
    // answers with nulls and still has a tab to open.
    if (!hasData) return;

    // Spent even when skipped below, so a response arriving late cannot fit the
    // range a moment after the user has settled somewhere.
    pendingBootFit = false;
    if (navigationType === "POP" && restored) return;
    applyShowAllRecords(target);
  }, [collectionID, hasData, target, navigationType, restored]);
}

/**
 * "1789 - 1799", or a single name when the whole range fits one tick.
 *
 * The granularity is whatever `fitIntervalsToSeconds` settled on, so it adapts
 * on its own: years for a decade, centuries for an era. `safeName` rather than
 * `getName` because the interval type is not known statically and `getName`
 * throws for one it has no format for.
 */
export function formatFittedRange(target: QuickIntervalTarget): string {
  const start = safeName(target.interval);
  if (!target.endInterval) return start;
  return `${start} - ${safeName(target.endInterval)}`;
}
